import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Home, Paintbrush, Zap, Layers, ArrowRight, CheckCircle2 } from 'lucide-react';

const pillars = [
  {
    icon: Home,
    title: 'Rénovation complète',
    desc: "Rénovation clé en main d'appartements, maisons, immeubles, commerces et bureaux. Un interlocuteur unique, de l'étude à la livraison.",
    points: ['Coordination tous corps d\'état', 'Gestion de A à Z', 'Suivi de chantier'],
  },
  {
    icon: Paintbrush,
    title: 'Rénovation intérieure',
    desc: 'Peinture, revêtements sols et murs, plâtrerie, cuisine, salle de bains et aménagement pour un intérieur entièrement remis à neuf.',
    points: ['Finitions soignées', 'Matériaux étudiés', 'Conseil agencement'],
  },
  {
    icon: Zap,
    title: 'Rénovation énergétique',
    desc: 'Isolation, VMC, menuiseries et amélioration du confort thermique pour réduire vos consommations et valoriser votre bien.',
    points: ['Isolation ITE / ITI', 'Confort thermique', 'Amélioration du DPE'],
    link: '/renovation-energetique',
  },
  {
    icon: Layers,
    title: 'Façade & ravalement',
    desc: 'Ravalement, traitement de fissures, ravalement à la chaux et isolation par l\'extérieur pour protéger et embellir le bâti.',
    points: ['Ravalement technique', 'Traitement fissures', 'Copropriétés & immeubles'],
  },
];

const Renovation = () => {
  const goToContact = () =>
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="renovation" className="py-24 bg-white scroll-mt-28">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-14">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-3 block">
            Rénovation
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
            Une entreprise de rénovation, pour tous vos projets
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            VIREO conçoit et réalise vos travaux de rénovation à Paris et en Île-de-France :
            rénovation complète, intérieure et énergétique, ainsi que façade et isolation.
            Particuliers, copropriétés et professionnels.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const inner = (
              <Card
                data-testid={`renovation-pillar-${i}`}
                className="group h-full border-slate-200 hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 bg-white"
              >
                <CardContent className="p-7 flex flex-col h-full">
                  <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4 flex-1">{p.desc}</p>
                  <ul className="space-y-1.5 mb-4">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  {p.link && (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-medium text-sm group-hover:gap-2 transition-all">
                      En savoir plus <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </CardContent>
              </Card>
            );
            return p.link ? (
              <Link key={i} to={p.link} className="block h-full">
                {inner}
              </Link>
            ) : (
              <div key={i} className="h-full">{inner}</div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={goToContact}
            data-testid="renovation-cta"
            className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Demander un devis gratuit
          </button>
        </div>
      </div>
    </section>
  );
};

export default Renovation;
